import { createContext, useContext, useMemo } from 'react'

/** Which agent a chat surface talks to. */
export enum AgentKind {
  INTERNAL = 'INTERNAL',
  ASK_CAI = 'ASK_CAI',
}

export interface AgentApi {
  kind: AgentKind
  /** REST root for this agent's conversations, no trailing slash. */
  basePath: string
  /**
   * Whether this agent defers its writes for a human to confirm. Only the
   * staff agent does — Ask Cai runs them inline, and its viewset has no
   * pending-action endpoints to poll.
   */
  confirmsWrites: boolean
}

const INTERNAL_API: AgentApi = {
  kind: AgentKind.INTERNAL,
  basePath: '/api/v1/insure/internal-conversations',
  confirmsWrites: true,
}

const ASK_CAI_API: AgentApi = {
  kind: AgentKind.ASK_CAI,
  basePath: '/api/v1/insure/ask-cai-conversations',
  confirmsWrites: false,
}

const API_BY_KIND: Record<AgentKind, AgentApi> = {
  [AgentKind.INTERNAL]: INTERNAL_API,
  [AgentKind.ASK_CAI]: ASK_CAI_API,
}

/**
 * Defaults to the internal agent so the staff surfaces — which predate this —
 * keep working without a provider.
 */
const AgentKindContext = createContext<AgentApi>(INTERNAL_API)

export function AgentKindProvider({ kind, children }: { kind: AgentKind; children: React.ReactNode }) {
  const value = useMemo(() => API_BY_KIND[kind], [kind])
  return <AgentKindContext.Provider value={value}>{children}</AgentKindContext.Provider>
}

/** Which agent the surrounding chat surface belongs to, and how to reach it. */
export function useAgentApi(): AgentApi {
  return useContext(AgentKindContext)
}
